import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AUTH_STATES } from '../contexts/AuthContext';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { 
    state, 
    error, 
    handleOAuthCallback, 
    resetError, 
    canRetry,
    isLoading,
    isAuthenticated 
  } = useAuth();
  const [localError, setLocalError] = useState(null);
  const [retryAttempts, setRetryAttempts] = useState(0);

  useEffect(() => {
    // Use sessionStorage to deduplicate across React Strict Mode double-invocation
    const PROCESSING_KEY = 'oauth_callback_processing_v2';

    const processCallback = async () => {
      try {
        setLocalError(null);
        
        // Check for OAuth errors in URL
        const urlFragment = window.location.hash;
        const queryError = searchParams.get('error');
        const fragmentError = urlFragment ? new URLSearchParams(urlFragment.substring(1)).get('error') : null;
        
        if (queryError || fragmentError) {
          const errorDesc = searchParams.get('error_description') || 'OAuth authentication failed';
          throw new Error(`OAuth error: ${queryError || fragmentError} - ${errorDesc}`);
        }

        if (!urlFragment) {
          throw new Error('No authentication data received. Please try signing in again.');
        }

        // Extract state from URL fragment
        const fragmentParams = new URLSearchParams(urlFragment.substring(1));
        const oauthState = fragmentParams.get('state');

        console.log('Processing OAuth callback...');
        
        // Handle the OAuth callback through the state machine
        const success = await handleOAuthCallback(urlFragment, oauthState);
        
        if (success) {
          sessionStorage.removeItem(PROCESSING_KEY);
          // Clear the URL fragment for security
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Short delay to ensure state is updated before navigation
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 100);
        } else {
          sessionStorage.removeItem(PROCESSING_KEY);
        }
        
      } catch (callbackError) {
        console.error('Auth callback error:', callbackError);
        sessionStorage.removeItem(PROCESSING_KEY);
        setLocalError(callbackError.message);
      }
    };

    // Guard: skip if already authenticated, no fragment, or already processing
    if (state === AUTH_STATES.AUTHENTICATED) return;
    if (!window.location.hash) return;
    if (sessionStorage.getItem(PROCESSING_KEY)) return;

    sessionStorage.setItem(PROCESSING_KEY, '1');
    processCallback();

    // Cleanup: remove flag if component unmounts before completion
    return () => {
      // Only clear if the current session key matches (avoid clearing a fresh attempt)
    };
  }, [searchParams, handleOAuthCallback, navigate, state]);

  // Handle successful authentication
  useEffect(() => {
    if (isAuthenticated && state === AUTH_STATES.AUTHENTICATED) {
      // Clear URL fragment and redirect
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, state, navigate]);
  
  // Prevent double processing by checking if already authenticated
  if (isAuthenticated) {
    // Already authenticated, redirect immediately
    window.history.replaceState({}, document.title, window.location.pathname);
    navigate('/', { replace: true });
    return null;
  }

  // Handle retry attempts
  const handleRetry = () => {
    // Clear all stale session flags and start a fresh OAuth flow from login
    sessionStorage.removeItem('oauth_callback_processed');
    sessionStorage.removeItem('oauth_callback_processing');
    sessionStorage.removeItem('oauth_callback_processing_v2');
    sessionStorage.removeItem('oauth_state');
    resetError();
    navigate('/login', { replace: true });
  };

  // Show error state
  if (error || localError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="dashboard-card p-8 text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">
            {error?.userMessage || localError || 'An unexpected error occurred during sign in.'}
          </p>
          
          {/* Show technical details for debugging if needed */}
          {(error?.message || localError) && (
            <details className="mb-4 text-sm text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">Technical Details</summary>
              <p className="mt-2 font-mono bg-gray-100 p-2 rounded">
                {error?.message || localError}
              </p>
            </details>
          )}
          
          <div className="flex gap-3 justify-center">
            {(canRetry || retryAttempts < 2) && (
              <button 
                onClick={handleRetry}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                disabled={isLoading}
              >
                {isLoading ? 'Retrying...' : `Try Again ${retryAttempts > 0 ? `(${retryAttempts}/2)` : ''}`}
              </button>
            )}
            <button 
              onClick={() => navigate('/login', { replace: true })}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show different loading messages based on state
  const getLoadingMessage = () => {
    switch (state) {
      case AUTH_STATES.OAUTH_CALLBACK:
        return {
          title: 'Processing Sign In',
          message: 'Verifying your authentication credentials...'
        };
      case AUTH_STATES.AUTHENTICATING:
        return {
          title: 'Completing Sign In',
          message: 'Setting up your account access...'
        };
      default:
        return {
          title: 'Signing You In',
          message: 'Please wait while we complete your authentication...'
        };
    }
  };

  const loadingContent = getLoadingMessage();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="dashboard-card p-8 text-center max-w-md">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-color mx-auto mb-6"></div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{loadingContent.title}</h2>
        <p className="text-gray-600 mb-4">{loadingContent.message}</p>
        
        {/* Progress indicator */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div className="bg-primary-color h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
        </div>
        
        {/* Cancel option for long waits */}
        {state === AUTH_STATES.OAUTH_CALLBACK && (
          <button 
            onClick={() => navigate('/login', { replace: true })}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel and return to login
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
